import { useState } from 'react';
import toast from 'react-hot-toast';
import { useMySubscription, useReportPayment } from '@/hooks/useSubscription';
import { useAuthStore } from '@/context/authStore';
import { formatPrice, formatDate } from '@/utils/format';
import {
  CreditCard,
  CalendarClock,
  CheckCircle,
  Clock,
  AlertTriangle,
  Copy,
  MessageCircle,
  Building2,
} from 'lucide-react';

const PLAN_LABELS: Record<string, string> = {
  basic: 'Básico',
  professional: 'Profesional',
  enterprise: 'Enterprise',
};

// Estado de pago derivado: si el vencimiento ya paso, mostrar "vencido"
// aunque el campo diga otra cosa.
//
// Durante la prueba gratuita no hay pago pendiente: lo que vence es la prueba, no una
// cuota, asi que el estado se toma de end_date y no de payment_status.
function derivePaymentState(
  status: string,
  paymentStatus: string,
  nextPaymentDate: string | null,
  endDate: string | null,
) {
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  if (status === 'trial') {
    if (endDate && new Date(endDate) < today) return 'trial_ended';
    return 'trial';
  }

  if (nextPaymentDate) {
    const due = new Date(nextPaymentDate);
    // El ciclo esta por pagar si el vencimiento cae en este mes o antes. No se mira
    // payment_status: queda clavado en "paid" apenas se confirma el primer pago y nada
    // lo devuelve a pendiente al empezar el mes siguiente.
    const cicloPorPagar =
      due.getFullYear() < today.getFullYear() ||
      (due.getFullYear() === today.getFullYear() && due.getMonth() <= today.getMonth());

    if (due < today && cicloPorPagar) return 'overdue';
    if (cicloPorPagar) return 'pending';
    return 'paid';
  }
  return paymentStatus;
}

const STATE_UI: Record<string, { label: string; className: string; Icon: typeof CheckCircle }> = {
  paid: { label: 'Al día', className: 'bg-green-100 text-green-700', Icon: CheckCircle },
  pending: { label: 'Pago pendiente', className: 'bg-amber-100 text-amber-700', Icon: Clock },
  overdue: { label: 'Vencido', className: 'bg-red-100 text-red-700', Icon: AlertTriangle },
  trial: { label: 'Prueba gratuita', className: 'bg-blue-100 text-blue-700', Icon: Clock },
  trial_ended: { label: 'Prueba finalizada', className: 'bg-red-100 text-red-700', Icon: AlertTriangle },
};

export default function MySubscription() {
  const { tenant } = useAuthStore();
  const { data: sub, isLoading, isError } = useMySubscription();
  const reportPayment = useReportPayment();
  const [copiedField, setCopiedField] = useState<string | null>(null);

  const copy = async (label: string, value: string) => {
    try {
      await navigator.clipboard.writeText(value);
      setCopiedField(label);
      toast.success(`${label} copiado`);
      setTimeout(() => setCopiedField(null), 1500);
    } catch {
      toast.error('No se pudo copiar');
    }
  };

  if (isLoading) {
    return <div className="p-8 text-center text-gray-500">Cargando tu suscripción…</div>;
  }

  if (isError || !sub) {
    return (
      <div className="max-w-lg mx-auto">
        <div className="card text-center">
          <CreditCard className="h-10 w-10 mx-auto text-gray-300 mb-3" />
          <h2 className="text-lg font-semibold text-gray-900 mb-1">Suscripción no configurada</h2>
          <p className="text-sm text-gray-500">
            Tu veterinaria todavía no tiene una suscripción cargada. Escribinos y la activamos.
          </p>
        </div>
      </div>
    );
  }

  const state = derivePaymentState(sub.status, sub.payment_status, sub.next_payment_date, sub.end_date);
  const stateUi = STATE_UI[state] || STATE_UI.pending;
  const StateIcon = stateUi.Icon;
  const enPrueba = state === 'trial' || state === 'trial_ended';

  const hasPaymentData = Boolean(sub.payment_alias || sub.cbu || sub.cvu);
  const canNotify = Boolean(sub.whatsapp_number);

  const handleNotify = async () => {
    // Registrar el aviso en el sistema (para que el superadmin lo vea)
    try {
      await reportPayment.mutateAsync();
    } catch {
      // Aunque falle el registro, igual dejamos abrir WhatsApp
    }
    const monto = formatPrice(sub.price_monthly);
    const venc = sub.next_payment_date ? ` (vencimiento ${formatDate(sub.next_payment_date)})` : '';
    const msg =
      `Hola! Soy ${tenant?.name || 'mi veterinaria'}. ` +
      `Aboné la suscripción de ANIMAvet por ${monto}${venc}. Adjunto el comprobante.`;
    const url = `https://wa.me/${sub.whatsapp_number}?text=${encodeURIComponent(msg)}`;
    window.open(url, '_blank');
  };

  const rows: { label: string; value: string | null }[] = [
    { label: 'Alias', value: sub.payment_alias },
    { label: 'CBU', value: sub.cbu },
    { label: 'CVU', value: sub.cvu },
    { label: 'Titular', value: sub.bank_holder },
  ];

  return (
    <div className="max-w-2xl mx-auto space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Mi Suscripción</h1>
        <p className="text-sm text-gray-500">Tu plan, vencimiento y cómo abonar.</p>
      </div>

      {/* Resumen */}
      <div className="card">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <p className="text-xs uppercase tracking-wide text-gray-400 font-semibold">Plan</p>
            <p className="text-lg font-bold text-gray-900">
              {PLAN_LABELS[sub.plan] || sub.plan}
            </p>
          </div>
          <div>
            <p className="text-xs uppercase tracking-wide text-gray-400 font-semibold">Monto mensual</p>
            <p className="text-lg font-bold text-gray-900">{formatPrice(sub.price_monthly)}</p>
            {enPrueba && (
              <p className="text-xs text-gray-500">Se empieza a abonar al terminar la prueba</p>
            )}
          </div>
          <span
            className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium ${stateUi.className}`}
          >
            <StateIcon className="h-4 w-4" />
            {stateUi.label}
          </span>
        </div>

        <div className="mt-4 pt-4 border-t grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="flex items-center gap-2 text-sm">
            <CalendarClock className="h-4 w-4 text-gray-400" />
            <span className="text-gray-500">
              {enPrueba ? 'Fin de la prueba:' : 'Próximo vencimiento:'}
            </span>
            <span className="font-medium text-gray-900">
              {enPrueba
                ? sub.end_date
                  ? formatDate(sub.end_date)
                  : '—'
                : sub.next_payment_date
                  ? formatDate(sub.next_payment_date)
                  : '—'}
            </span>
          </div>
          <div className="flex items-center gap-2 text-sm">
            <CheckCircle className="h-4 w-4 text-gray-400" />
            <span className="text-gray-500">Último pago:</span>
            <span className="font-medium text-gray-900">
              {sub.last_payment_date ? formatDate(sub.last_payment_date) : '—'}
            </span>
          </div>
        </div>

        {sub.payment_reported_at && (
          <div className="mt-4 flex items-start gap-2 rounded-lg bg-blue-50 text-blue-700 px-3 py-2 text-sm">
            <Clock className="h-4 w-4 mt-0.5 flex-shrink-0" />
            <span>
              Informaste el pago el {formatDate(sub.payment_reported_at)}. Estamos verificándolo.
            </span>
          </div>
        )}
      </div>

      {/* Datos para pagar */}
      <div className="card">
        <h2 className="text-base font-semibold text-gray-900 mb-3 flex items-center gap-2">
          <Building2 className="h-5 w-5 text-taupe-500" />
          Datos para transferir
        </h2>

        {!hasPaymentData ? (
          <p className="text-sm text-gray-500">
            Todavía no se cargaron los datos de cobro. Escribinos para coordinar el pago.
          </p>
        ) : (
          <div className="space-y-2">
            {rows
              .filter((r) => r.value)
              .map((r) => (
                <div
                  key={r.label}
                  className="flex items-center justify-between gap-3 rounded-lg border border-gray-100 bg-gray-50 px-3 py-2"
                >
                  <div className="min-w-0">
                    <p className="text-xs text-gray-400">{r.label}</p>
                    <p className="text-sm font-medium text-gray-900 truncate">{r.value}</p>
                  </div>
                  <button
                    onClick={() => copy(r.label, r.value as string)}
                    className="p-2 rounded-md hover:bg-gray-200 text-gray-500 flex-shrink-0"
                    title={`Copiar ${r.label}`}
                  >
                    <Copy className="h-4 w-4" />
                    {copiedField === r.label && <span className="sr-only">Copiado</span>}
                  </button>
                </div>
              ))}
          </div>
        )}

        <div className="mt-4">
          <button
            onClick={handleNotify}
            disabled={!canNotify || reportPayment.isPending}
            className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg font-medium text-white bg-green-600 hover:bg-green-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <MessageCircle className="h-5 w-5" />
            {reportPayment.isPending ? 'Abriendo…' : 'Avisar pago por WhatsApp'}
          </button>
          {!canNotify && (
            <p className="mt-2 text-xs text-gray-400">
              (El número de WhatsApp para avisos todavía no fue configurado.)
            </p>
          )}
        </div>
      </div>
    </div>
  );
}
